import React from "react";
import { LifeBuoy, BookOpen, Radio, Mail, MessageCircle, ExternalLink } from "lucide-react";

function PageShell({ icon: Icon, label, title, subtitle, children }) {
  return (
    <div className="max-w-4xl">
      <div className="mb-5 flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center border border-[#1a1a1a] bg-[#0a0a0a] rounded-xl text-[#9D4EDD]">
          <Icon size={18} />
        </div>
        <div>
          <div className="gc-label">{label}</div>
          <h1 className="font-heading text-2xl md:text-3xl font-black leading-none mt-1">
            {title}
          </h1>
          {subtitle && <p className="text-sm text-[#A1A1AA] mt-1">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

export function Help() {
  return (
    <PageShell
      icon={LifeBuoy}
      label="KAYNAKLAR"
      title={<><span className="text-[#9D4EDD]">Yardım</span> Merkezi</>}
      subtitle="Sıkça sorulan sorular ve destek kanalları."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <a
          href="mailto:destek@gulyan.local"
          onClick={(e) => e.preventDefault()}
          className="gc-card p-4 hover:border-[#9D4EDD]/40 transition-colors"
          data-testid="help-email"
        >
          <div className="flex items-center gap-2 mb-2">
            <Mail size={14} className="text-[#9D4EDD]" />
            <div className="font-heading font-bold">E-posta Desteği</div>
          </div>
          <div className="text-xs text-[#A1A1AA]">
            destek@gulyan.local — 24 saat içinde yanıt.
          </div>
        </a>
        <a
          href="#"
          onClick={(e) => e.preventDefault()}
          className="gc-card p-4 hover:border-[#9D4EDD]/40 transition-colors"
          data-testid="help-live"
        >
          <div className="flex items-center gap-2 mb-2">
            <MessageCircle size={14} className="text-[#9D4EDD]" />
            <div className="font-heading font-bold">Canlı Destek</div>
          </div>
          <div className="text-xs text-[#A1A1AA]">
            Silver/Gold üyeliklerde 7/24 canlı destek.
          </div>
        </a>
      </div>

      <div className="mt-4 gc-card p-4">
        <div className="gc-label mb-3">HIZLI REHBERLER</div>
        <ul className="space-y-2 text-sm text-[#EDEDED]">
          {[
            "Bir Discord ID nasıl sorgulanır?",
            "Üyelik BTC ile nasıl satın alınır?",
            "TXID nedir ve nereden bulunur?",
            "Günlük sorgu limitim ne zaman sıfırlanır?",
            "Sorgu sonuçlarını CSV'ye nasıl aktarırım? (Silver+)",
            "İki faktörlü doğrulama (2FA) — Admin Panel → Güvenlik sekmesinden aktifleştir.",
          ].map((q) => (
            <li key={q} className="flex items-start gap-2">
              <span className="text-[#9D4EDD] mt-0.5">›</span>
              <span>{q}</span>
            </li>
          ))}
        </ul>
      </div>
    </PageShell>
  );
}

export function ApiDocs() {
  return (
    <PageShell
      icon={BookOpen}
      label="KAYNAKLAR"
      title={<><span className="text-[#9D4EDD]">API</span> Dokümanı</>}
      subtitle="Programatik erişim — Gold plan gereklidir."
    >
      <div className="gc-card p-5">
        <div className="gc-label mb-2">TEMEL BİLGİLER</div>
        <p className="text-sm text-[#A1A1AA]">
          Tüm endpoint'ler <code className="font-mono text-[#9D4EDD]">/api</code> önekiyle
          başlar. Kimlik doğrulama httpOnly cookie ya da{" "}
          <code className="font-mono text-[#9D4EDD]">Authorization: Bearer &lt;token&gt;</code>{" "}
          başlığı ile yapılır.
        </p>

        <div className="gc-label mt-5 mb-2">ÖRNEK: DISCORD SORGUSU</div>
        <pre className="font-mono text-xs text-[#EDEDED] bg-[#050505] border border-[#141414] p-3 overflow-x-auto rounded-lg">
{`POST /api/checker/discord/query
Content-Type: application/json

{ "query": "92433932011724800" }`}
        </pre>

        <div className="gc-label mt-5 mb-2">YANIT</div>
        <pre className="font-mono text-xs text-[#EDEDED] bg-[#050505] border border-[#141414] p-3 overflow-x-auto rounded-lg">
{`{
  "platform": "discord",
  "query": "92433932011724800",
  "count": 2,
  "results": [ { "discord_id": "…", "email": "…", "ip": "…", … } ]
}`}
        </pre>

        <div className="gc-label mt-5 mb-2">DESTEKLENEN PLATFORMLAR</div>
        <div className="flex flex-wrap gap-1.5">
          {["discord", "instagram", "snapchat", "tiktok", "facebook", "github", "telegram", "twitter"].map(
            (p) => (
              <span key={p} className="gc-badge">
                {p}
              </span>
            ),
          )}
        </div>
      </div>
    </PageShell>
  );
}

export function Community() {
  return (
    <PageShell
      icon={Radio}
      label="KAYNAKLAR"
      title={<><span className="text-[#9D4EDD]">Discord</span> Topluluğu</>}
      subtitle="Duyurular, güncelleme notları ve destek."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="gc-card p-5">
          <div className="gc-label mb-2">DISCORD SUNUCUMUZ</div>
          <div className="font-heading text-lg font-bold mb-2">
            Anlık haberler + Ücretsiz sorgu haklarını kap
          </div>
          <p className="text-sm text-[#A1A1AA] mb-4">
            Sunucumuza katılan ilk 500 kullanıcıya 1 hafta ücretsiz Bronze
            üyelik veriyoruz. Katıldıktan sonra Profil sayfasından Discord
            kullanıcı adını admin'e ilet.
          </p>
          <a
            href="https://discord.gg/KbWUeh6SP2"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="community-discord-link"
            className="gc-btn-primary inline-flex items-center gap-2"
          >
            KATIL <ExternalLink size={12} />
          </a>
        </div>

        <div className="gc-card p-5">
          <div className="gc-label mb-2">TOPLULUK KURALLARI</div>
          <ul className="space-y-2 text-sm text-[#EDEDED]">
            {[
              "Yasa dışı içerik / dağıtım paylaşımı yasaktır.",
              "Kendi verinizi test amaçlı bile olsa alenen paylaşmayın.",
              "API anahtarınızı hiç kimseyle paylaşmayın.",
              "Diğer üyelere saygılı ol.",
            ].map((r) => (
              <li key={r} className="flex items-start gap-2">
                <span className="text-[#9D4EDD] mt-0.5">›</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </PageShell>
  );
}
